import React, { useEffect, useState } from "react";
import { FormikType } from "../../../../types/types";
import { InitialAuditionStateType } from "../../../../assets/data/initialState";
import { useIADispatch, useIASelector } from "../../../../redux/hooks";
import {
  addPerformerTypeFilter,
  deletePerformerTypeFilter,
  selectedPerformerTypeFilterSelector,
  selectedPerformerTypeFiltersSelector,
  setSelectedPerformerTypeFilter,
  updateSelectedPerformerTypeFilters,
} from "../../../../redux/slices/performerTypeFiltersSlice";
import {
  useCreatePerformerTypeFilterMutation,
  useDeletePerformerTypeFilterMutation,
  useUpdatePerformerTypeFilterMutation,
} from "../../../../services/PerformerTypeFiltersServices/PerformerTypeFiltersServices";
import { useFormik } from "formik";
import { PerformerTypeFilterTitleSchema } from "../../../../utils/validationSchema";
import asyncTimeout from "../../../../utils/asyncTimeout";
import { toast } from "react-toastify";
import { isErrorWithMessage } from "../../../../services/helper";
import TextInput from "../../inputfields/TextInput";
import CustomButton from "../../buttons/CustomButton";

type Props = FormikType<InitialAuditionStateType>;

export default function PerformerTypeFilterModalEdit({ formik }: Props) {
  const selectedPerformerTypeFilter = useIASelector(
    selectedPerformerTypeFilterSelector
  );
  const selectedPerformerTypeFilters = useIASelector(
    selectedPerformerTypeFiltersSelector
  );
  const dispatch = useIADispatch();

  const [createPerformerTypeFilter] = useCreatePerformerTypeFilterMutation();
  const [updatePerformerTypeFilter] = useUpdatePerformerTypeFilterMutation();
  const [deletePerformerTypeFilterQuery] =
    useDeletePerformerTypeFilterMutation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [isUpdate, setIsUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (!isUpdate) return;
    formik.setFieldValue("performerTypeFilters", selectedPerformerTypeFilters);
    setIsUpdate(false);
  }, [selectedPerformerTypeFilters, isUpdate]);

  useEffect(() => {
    setFieldTouched("title", false);
    setFieldError("title", "");

    if (!selectedPerformerTypeFilter) {
      setFieldValue("title", "");
      return;
    }
    setFieldValue("title", selectedPerformerTypeFilter.title);
  }, [selectedPerformerTypeFilter]);

  const onSave = async () => {
    setIsLoading(true);
    try {
      const [response] = await Promise.all([
        createPerformerTypeFilter({
          title: values.title,
        }).unwrap(),
        asyncTimeout(300),
      ]);

      if (response) {
        toast.success("Successfully created");
        dispatch(addPerformerTypeFilter(response));
      }
    } catch (error) {
      toast.error(
        isErrorWithMessage(error)
          ? error.data.data.message
          : "Something went wrong. Please try again"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdate = async () => {
    if (!selectedPerformerTypeFilter) return;

    setIsLoading(true);

    try {
      const [response] = await Promise.all([
        updatePerformerTypeFilter({
          id: selectedPerformerTypeFilter.id,
          title: values.title,
        }).unwrap(),
        asyncTimeout(300),
      ]);

      if (response) {
        toast.success("Successfully updated");
        dispatch(updateSelectedPerformerTypeFilters(response));
        setIsUpdate(true);
      }
    } catch (error) {
      console.log("error", error);
      toast.error(
        isErrorWithMessage(error)
          ? error.data.data.message
          : "Something went wrong. Please try again"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onDelete = async () => {
    setIsDeleting(true);
    if (!selectedPerformerTypeFilter) return;
    try {
      const [response] = await Promise.all([
        deletePerformerTypeFilterQuery({
          id: selectedPerformerTypeFilter.id,
        }).unwrap(),
        asyncTimeout(300),
      ]);

      if (response) {
        toast.success("Successfully deleted");
        dispatch(setSelectedPerformerTypeFilter(null));
        dispatch(deletePerformerTypeFilter(response.id));
        setIsUpdate(true);
      }
    } catch (error) {
      toast.error(
        isErrorWithMessage(error)
          ? error.data.data.message
          : "Something went wrong. Please try again"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const {
    values,
    touched,
    errors,
    setFieldValue,
    handleSubmit,
    setFieldTouched,
    setFieldError,
  } = useFormik({
    initialValues: {
      title: "",
    },
    onSubmit: selectedPerformerTypeFilter ? onUpdate : onSave,
    validationSchema: PerformerTypeFilterTitleSchema,
  });

  return (
    <div className="h-full w-full justify-between flex flex-col border-l-2 border-black">
      <div className="mx-4 ">
        <p className="font-Switzer font-bold text-black text-2xl my-4">
          {selectedPerformerTypeFilter
            ? "Update Performer Type"
            : "Create Performer Type"}
        </p>
        <p className="font-Switzer font-normal text-black text-base">
          {selectedPerformerTypeFilter
            ? "Update performer type to the library."
            : "Add a new performer type to the library."}
        </p>
        <TextInput
          label="Performer Type"
          name="Performer Type"
          type="text"
          value={values.title}
          required={true}
          onChange={(text) => setFieldValue("title", text)}
          isTouched={touched.title}
          error={errors.title}
        />
        <CustomButton
          text={selectedPerformerTypeFilter ? "Update Type" : "Add Type"}
          className="medium-green-button mt-6"
          onClick={handleSubmit}
          isLoading={isLoading}
          disabled={isLoading || isDeleting}
        />
      </div>
      <div>
        <CustomButton
          text="Delete Type"
          onClick={onDelete}
          className="medium-white-button mb-4 ml-4"
          loaderColor="black"
          isLoading={isDeleting}
          disabled={isLoading || isDeleting}
        />
      </div>
    </div>
  );
}
